import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Skeleton, TextField, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useSnackbar } from 'notistack'
import palette from '../../../../theme/palette'
import { useNabdActionMutation, useNabdCaseQuery, useNabdMastersQuery } from '../../../redux/ptlNabdApiSlice'
import { OWNERS, SUB_TEAM_LABEL, TEAM_LABEL, canAct, rolesLabel } from '../constants'
import { Dash, FormField, LateTag, OwnerChip, sectionTitleSx } from '../eddBreachUi'
import { fmtDT } from '../eddBreachUtils'
import AttributionForm, { emptyForm, limitsOf, mappedKam, validate } from './AttributionForm'
import HoldSplitBar from './HoldSplitBar'
import JourneyTimeline from './JourneyTimeline'

/**
 * Dialog for one EDD breach (two columns, as in the artifact): journey on the left, the action on the right.
 *  - undelivered        → Attribute / Update (a KAM on a client-side shipment gets the Reply form instead)
 *  - delivered, pending → Close (PTL Ops only)
 *  - delivered, closed  → read-only record; Reopen for senior members
 * "Acting as" is the logged-in user only (PTL team, 08-Oct-2026): PTL Ops or a KAM for an attribution / update, a KAM
 * once the shipment is with the KAM (client attributable), PTL Ops for closure.
 */
export default function CaseDrawer({ caseId, open, onClose }) {
  const { enqueueSnackbar } = useSnackbar()
  // currentData, not data: while another shipment loads, `data` still holds the previous one
  const { currentData: detail, error } = useNabdCaseQuery(caseId, { skip: !open || !caseId })
  const { data: masters, error: mastersError } = useNabdMastersQuery(undefined, { skip: !open })
  const [runAction, { isLoading: saving }] = useNabdActionMutation()

  const row = detail?.row
  const me = masters?.me
  const locked = !!row?.locked
  const delivered = row?.breachType === 'D'
  // the action follows the shipment: delivered → close; with the KAM → the KAM replies; otherwise PTL Ops attributes
  const mode = delivered ? 'close' : (row?.owner === 'CLIENT' ? 'reply' : 'attribute')
  // who may act: PTL Ops and KAMs on an attribution; KAMs only once it is with the KAM; PTL Ops only at closure
  const actorRoles = mode === 'reply' ? ['KAM'] : mode === 'close' ? ['PTL_OPS'] : ['PTL_OPS', 'KAM']
  const actor = me?.active && actorRoles.some((r) => canAct(me, r)) ? me : null

  const [form, setForm] = useState(emptyForm)
  const [errors, setErrors] = useState({})
  const [serverError, setServerError] = useState('')
  const [reopenOpen, setReopenOpen] = useState(false)

  // pre-fill from the current attribution whenever the case or the form type changes
  useEffect(() => {
    if (!row || !masters) return
    setErrors({})
    setServerError('')
    if (mode === 'reply') {
      const raiser = (masters.team || []).find((t) => t.id === detail.raisedByMemberId && canAct(t, 'PTL_OPS') && t.active)
      setForm({ ...emptyForm, assigneeMemberId: raiser ? raiser.id : '' })
      return
    }
    // after a KAM hand-back the old request no longer describes the delay: start the next attribution from a blank reason
    if (mode === 'attribute' && row.replyCode) {
      setForm({ ...emptyForm, assigneeMemberId: (masters.team || []).some((t) => t.id === row.assignedMemberId && t.active) ? row.assignedMemberId : '' })
      return
    }
    const owner = mode === 'close' ? (detail.suggestedFinalOwner || row.owner || '') : (row.owner || '')
    const reason = (masters.reasons || []).find((r) => r.code === row.reasonCode)
    setForm({
      ...emptyForm,
      reasonCode: row.reasonCode || null,
      owner: row.reasonCode ? (owner || reason?.owner || '') : '',
      overrideRemark: detail.overrideRemark || '',
      pendingWith: row.pendingWith || '',
      // an inactive owner can't be re-assigned, so don't pre-fill them
      assigneeMemberId: (masters.team || []).some((t) => t.id === row.assignedMemberId && t.active)
        ? row.assignedMemberId : (row.owner === 'CLIENT' ? mappedKam(row, masters) : ''),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id, row?.version, mode, masters])

  const ctx = useMemo(() => ({ reasons: masters?.reasons || [], suggestedFinalOwner: detail?.suggestedFinalOwner, limits: limitsOf(masters) }), [masters, detail])
  const noActor = !actor || !masters

  const submit = async () => {
    const errs = validate(mode, form, ctx)
    setErrors(errs)
    if (Object.keys(errs).length || noActor) return
    const action = mode === 'reply' ? 'reply' : mode
    const body = {
      ...form,
      version: row.version,
    }
    try {
      await runAction({ id: caseId, action, body }).unwrap()
      enqueueSnackbar(
        mode === 'reply' ? 'Sent back to operations. Prozo attributable from now.'
          : mode === 'close' ? `Closed, attributed to ${OWNERS[form.owner].short}. The record is locked.`
            : `Attributed to ${OWNERS[form.owner].short}`, { variant: 'success' })
      onClose(true)
    } catch (e) {
      const msg = e?.data?.message || e?.message || 'Something went wrong'
      setServerError(msg)
      enqueueSnackbar(msg, { variant: 'error' })
    }
  }

  const title = locked ? 'Closed record, locked' : mode === 'close' ? 'Close and record the final attribution'
    : mode === 'reply' ? 'Reply and send back to operations' : row?.owner ? 'Update this delay' : 'Attribute this delay'

  const lateLabel = row ? (delivered ? `+${row.lateDays}d late` : `+${row.lateDays}d past EDD`) : ''

  return (
    <Dialog open={open} onClose={() => onClose(false)} scroll='paper' maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 940, maxHeight: '92dvh', m: 2, borderRadius: '8px' } }}>
      {/* header: what this is, which shipment (artifact .dlg-h) */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: '16px 20px', borderBottom: `1px solid ${palette.grey[300]}` }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
          {row && (
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5, lineHeight: 1.6 }}>
              <Box component='b' sx={{ color: 'primary.main' }}>{row.awb || row.orderNo}</Box> · {row.merchantName} · {row.pickupPin} to {row.dropPin} · {row.courierName || 'no courier'} · EDD {fmtDT(row.edd)} ·{' '}
              <Box component='b' sx={{ color: delivered ? palette.warning.darker : palette.error.dark }}>{lateLabel}</Box>
            </Typography>
          )}
        </Box>
        <IconButton size='small' onClick={() => onClose(false)} aria-label='Close'><CloseIcon fontSize='small' /></IconButton>
      </DialogTitle>

      {/* body: one scroll, two columns 1fr / 1.25fr (artifact .dlg-b.split) */}
      <DialogContent sx={{ p: '20px !important' }}>
        {error && <Alert severity='error'>{error?.data?.message || error?.message || 'Could not load this shipment.'}</Alert>}
        {!error && mastersError && (
          <Alert severity='error' sx={{ mb: 2 }}>{mastersError?.data?.message || mastersError?.message || 'Could not load the reasons and the PTL team. Close and try again.'}</Alert>
        )}
        {!error && (!row ? <Skeleton variant='rectangular' height={320} /> : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.25fr' }, gap: '20px', alignItems: 'start' }}>
            {/* left: what happened */}
            <Box sx={{ minWidth: 0 }}>
              {delivered && (
                <Box sx={{ mb: 1.5, p: '14px', borderRadius: '6px', bgcolor: palette.grey[100], border: `1px solid ${palette.grey[300]}` }}>
                  {[['AWB', row.awb], ['Merchant', row.merchantName], ['Carrier', row.courierName], ['Lane', `${row.pickupPin || ''} to ${row.dropPin || ''}`],
                    ['EDD', fmtDT(row.edd)], ['Delivered', fmtDT(row.deliveredAt)]].map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: '5px', borderBottom: `1px solid ${palette.grey[200]}` }}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{k}</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, textAlign: 'right' }}>{v || '—'}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: '5px' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Days late</Typography>
                    <LateTag breachType='D' lateDays={row.lateDays} />
                  </Box>
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={sectionTitleSx}>Who held this shipment</Typography>
                    <HoldSplitBar split={detail.holdSplit} />
                  </Box>
                </Box>
              )}
              <Typography sx={sectionTitleSx}>Journey and audit trail</Typography>
              <Box sx={{ p: '14px', borderRadius: '6px', bgcolor: palette.grey[100], border: `1px solid ${palette.grey[300]}`, maxHeight: 340, overflowY: 'auto' }}>
                <JourneyTimeline events={detail.events} team={masters?.team} />
              </Box>
              <Box sx={{ mt: 1.25 }}><OpenRequirement row={row} /></Box>
            </Box>

            {/* right: the action */}
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {locked ? (
                <LockedRecord row={row} detail={detail} canReopen={!!me?.senior && me?.active} />
              ) : (
                <>
                  {masters && <ActingAs me={me} />}
                  {masters && !actor && (
                    <Alert severity='warning' sx={{ py: 0, fontSize: 12 }}>
                      {!me ? 'You are not on the PTL team list, so you cannot act on this shipment. Ask the PTL lead to add you.'
                        : !me.active ? 'You are inactive in the PTL team list, so you cannot act on this shipment.'
                          : mode === 'reply' ? `This shipment is with the ${TEAM_LABEL.KAM}. Only a KAM can reply and send it back to operations.`
                            : mode === 'close' ? `Only ${TEAM_LABEL.PTL_OPS} can close a delivered shipment.`
                              : `Only ${TEAM_LABEL.PTL_OPS} or a ${TEAM_LABEL.KAM} can attribute this shipment.`}
                    </Alert>
                  )}
                  {serverError && <Alert severity='error' sx={{ py: 0, fontSize: 12 }}>{serverError}</Alert>}
                  <AttributionForm mode={mode} row={row} masters={masters} value={form} onChange={setForm} errors={errors}
                    suggestedFinalOwner={detail.suggestedFinalOwner} disabled={saving || noActor} />
                  {mode === 'close' && actor && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Closed by <b>{actor.name}</b> (PTL Operations).</Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        ))}
      </DialogContent>

      {/* footer: always visible */}
      <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: `1px solid ${palette.grey[300]}` }}>
        <Button onClick={() => onClose(false)} disabled={saving}>{locked ? 'Close' : 'Cancel'}</Button>
        {locked && !!me?.senior && me?.active && (
          <Button color='error' variant='outlined' onClick={() => setReopenOpen(true)}>Reopen record</Button>
        )}
        {!locked && row && (
          <LoadingButton variant='contained' loading={saving} onClick={submit} disabled={noActor}>
            {mode === 'close' ? 'Mark as closed' : mode === 'reply' ? 'Reply and send back' : 'Save attribution'}
          </LoadingButton>
        )}
      </DialogActions>

      {row && (
        <ReopenDialog open={reopenOpen} row={row} me={me} limits={limitsOf(masters)} onClose={() => setReopenOpen(false)}
          onDone={() => { setReopenOpen(false); onClose(true) }} />
      )}
    </Dialog>
  )
}

function OpenRequirement({ row }) {
  // artifact .openreq: amber card under the journey
  const tone = row.owner ? palette.warning : palette.error
  const handedBack = !!row.replyCode
  return (
    <Box sx={{ p: '10px 12px', borderRadius: '6px', border: `1px solid ${tone.light}`, bgcolor: tone.lighter }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: tone.darker, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        {!row.owner ? 'Not attributed' : handedBack ? 'Handed back to operations' : row.breachType === 'D' ? 'Last attribution' : 'Open requirement'}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 0.4 }}>{row.owner ? row.currentReasonLabel : 'This delay has no owner yet'}</Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.4, lineHeight: 1.5 }}>
        {!row.owner
          ? 'Pick a reason to attribute it and route it to the team that owns the next action.'
          : <>
            {handedBack && <>Raised by operations as: {row.requestLabel}. The client side is cleared. </>}
            {OWNERS[row.owner]?.label}.
            {row.pendingWithLabel && ` Pending with ${row.pendingWithLabel} for ${row.pendingDays} day${row.pendingDays === 1 ? '' : 's'}.`}
            {row.assignedMemberName && ` Owner ${row.assignedMemberName}.`}
            {row.breachType === 'D' && !row.locked && ' Confirm the final attribution at closure.'}
          </>}
      </Typography>
    </Box>
  )
}

/** "Acting as": the logged-in user's own team record (name, team, login email), read-only. */
function ActingAs({ me }) {
  return (
    <FormField label='Acting as' hint='The action is recorded under your name.'>
      <Box sx={{ p: '8px 12px', borderRadius: '6px', border: `1px solid ${palette.grey[300]}`, bgcolor: palette.grey[100] }}>
        {me ? (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              {me.name}
              <Typography component='span' sx={{ ml: 1, fontSize: 11, color: 'text.secondary' }}>{me.subTeam ? SUB_TEAM_LABEL[me.subTeam] : rolesLabel(me)}</Typography>
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{me.email}</Typography>
          </>
        ) : <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>You are not on the PTL team list</Typography>}
      </Box>
    </FormField>
  )
}

function LockedRecord({ row, detail, canReopen }) {
  return (
    <Box>
      <Alert icon={<LockOutlinedIcon />} severity='success' sx={{ mb: 2 }}>
        <b>This record is locked.</b> Closed by {row.closedByName || 'PTL Operations'} on {fmtDT(row.closedAt)}. The attribution, root cause,
        reason, remarks and owner can no longer be changed. {canReopen ? 'As a senior member you can reopen it with a written reason.' : 'Only a senior member can reopen it.'}
      </Alert>
      {[['Final attribution', <OwnerChip key='o' owner={row.owner} />],
        ['Reason', row.requestLabel || <Dash key='d' />],
        ['Overridden', row.overridden ? 'Yes' : 'No'],
        ['Final remarks', detail.lastRemarks || <Dash key='r' />]].map(([k, v]) => (
        <Box key={k} sx={{ display: 'flex', gap: 2, py: 1, borderBottom: `1px solid ${palette.grey[200]}` }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', width: 130, flexShrink: 0 }}>{k}</Typography>
          <Box sx={{ fontSize: 12 }}>{v}</Box>
        </Box>
      ))}
    </Box>
  )
}

function ReopenDialog({ open, row, me, limits, onClose, onDone }) {
  const { enqueueSnackbar } = useSnackbar()
  const [why, setWhy] = useState('')
  const [err, setErr] = useState('')
  const [runAction, { isLoading }] = useNabdActionMutation()
  const { minReopenReason = 0, maxReopenReason } = limits
  useEffect(() => { if (open) { setWhy(''); setErr('') } }, [open])

  const submit = async () => {
    if (why.trim().length < minReopenReason) { setErr(`Give the reason for reopening, at least ${minReopenReason} characters. It goes into the record.`); return }
    try {
      await runAction({ id: row.id, action: 'reopen', body: { remarks: why.trim(), version: row.version } }).unwrap()
      enqueueSnackbar(`Reopened by ${me?.name}. The record is editable until it is closed again.`, { variant: 'success' })
      onDone()
    } catch (e) {
      setErr(e?.data?.message || e?.message || 'Something went wrong')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><WarningAmberIcon color='error' /> Reopen this closed record</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
          {row.awb} · closed by {row.closedByName} on {fmtDT(row.closedAt)}. The record returns to pending, the reason and attribution are kept
          so the next closure starts from them, and this reopen is written into the trail under your name ({me?.name}).
        </Typography>
        <TextField fullWidth multiline minRows={3} autoFocus label='Why is this being reopened' required value={why}
          onChange={(e) => setWhy(e.target.value)} inputProps={{ maxLength: maxReopenReason }}
          error={!!err} helperText={err || `${why.trim().length}/${maxReopenReason ?? '…'} · minimum ${minReopenReason} characters`} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <LoadingButton color='error' variant='contained' loading={isLoading} onClick={submit} disabled={!maxReopenReason}>Reopen record</LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
